/**
 * SSuM (Saarv Sustainability Matrix) Module
 * Strategic Maturity Assessment for Board Advisory
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  TrendingUp, Target, AlertTriangle, CheckCircle, ArrowRight,
  BarChart3, Activity, Zap, Droplets, Users, Database,
  FileText, Download, RefreshCw, Sparkles, Award, ChevronRight
} from 'lucide-react';
import { useAlert } from '../../context/AlertContext';

const API = process.env.REACT_APP_BACKEND_URL;

const SSuMModule = ({ study }) => {
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [assessment, setAssessment] = useState(null);
  const [roadmap, setRoadmap] = useState(null);
  const [gapAnalysis, setGapAnalysis] = useState(null);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiContent, setAiContent] = useState(null);

  useEffect(() => {
    if (study?.id) {
      fetchAssessment();
    }
  }, [study?.id]);

  const fetchAssessment = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    try {
      const [assessmentRes, roadmapRes] = await Promise.all([
        axios.get(`${API}/api/ssum/${study.id}/assessment`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/ssum/${study.id}/roadmap`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setAssessment(assessmentRes.data);
      setRoadmap(roadmapRes.data);
    } catch (error) {
      console.error('Error fetching SSuM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGapAnalysis = async (dimension) => {
    const token = localStorage.getItem('token');
    try {
      const res = await axios.get(`${API}/api/ssum/${study.id}/gap-analysis?dimension=${dimension}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGapAnalysis(res.data);
      setSelectedDimension(dimension);
    } catch (error) {
      console.error('Error fetching gap analysis:', error);
    }
  };

  const generateAIAdvisory = async (type) => {
    setGeneratingAI(true);
    const token = localStorage.getItem('token');
    try {
      const res = await axios.post(`${API}/api/ssum/${study.id}/ai-advisory?prompt_type=${type}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAiContent(res.data);
    } catch (error) {
      showError('Error generating AI advisory: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingAI(false);
    }
  };

  const getDimensionIcon = (dimension) => {
    const icons = {
      governance_leadership: Target,
      climate_energy: Zap,
      resource_efficiency: Droplets,
      social_people: Users,
      data_digital: Database
    };
    return icons[dimension] || BarChart3;
  };

  const getLevelColor = (level) => {
    if (level >= 5) return 'text-green-600 bg-green-50 border-green-200';
    if (level >= 3) return 'text-blue-600 bg-blue-50 border-blue-200';
    return 'text-orange-600 bg-orange-50 border-orange-200';
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <span className="text-green-600">↗</span>;
    if (trend === 'declining') return <span className="text-red-600">↘</span>;
    return <span className="text-gray-600">→</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mb-4"></div>
          <p className="text-gray-600">Calculating maturity assessment...</p>
        </div>
      </div>
    );
  }

  if (!assessment) {
    return <div className="text-center py-12">No assessment data available</div>;
  }

  const dimensions = assessment.dimensions;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">SSuM Strategic Maturity Assessment</h2>
            <p className="text-purple-100">Saarv Sustainability Matrix - Board Advisory Dashboard</p>
          </div>
          <div className="text-right">
            <div className="text-5xl font-bold mb-1">Level {assessment.overall_level}</div>
            <div className="text-sm text-purple-200">{assessment.overall_description}</div>
          </div>
        </div>
      </div>

      {/* Overall Progress */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Overall Maturity Progress</h3>
          <span className="text-2xl font-bold text-purple-600">{assessment.completion_percentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-gradient-to-r from-purple-600 to-indigo-600 h-4 rounded-full transition-all"
            style={{ width: `${assessment.completion_percentage}%` }}
          />
        </div>
        <p className="text-sm text-gray-600 mt-2">
          Score: {assessment.total_score} / {assessment.max_score} points across 5 dimensions
        </p>
      </div>

      {/* Dimensions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Object.entries(dimensions).map(([key, data]) => {
          const Icon = getDimensionIcon(key);
          return (
            <div
              key={key}
              className={`rounded-xl border-2 p-6 cursor-pointer hover:shadow-lg transition-all ${getLevelColor(data.level)}`}
              onClick={() => fetchGapAnalysis(key)}
            >
              <div className="flex items-start justify-between mb-4">
                <Icon className="w-8 h-8" />
                <div className="text-right">
                  <div className="text-3xl font-bold">Lv {data.level}</div>
                  <div className="text-xs mt-1">of 6</div>
                </div>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">
                {key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </h4>
              <p className="text-xs text-gray-600 mb-3">{data.description}</p>
              
              {/* Indicators */}
              <div className="space-y-1">
                {data.indicators.slice(0, 2).map((indicator, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs">
                    <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{indicator}</span>
                  </div>
                ))}
                {data.indicators.length > 2 && (
                  <p className="text-xs text-gray-500 italic">+{data.indicators.length - 2} more indicators</p>
                )}
              </div>
              
              <div className="mt-4 pt-4 border-t border-current/20">
                <button className="text-sm font-medium flex items-center gap-1 hover:underline">
                  View Gap Analysis <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* AI Advisory Section */}
      <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Sparkles className="w-6 h-6 text-indigo-600" />
            <h3 className="text-lg font-bold text-gray-900">AI Board Advisory</h3>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => generateAIAdvisory('executive_summary')}
              disabled={generatingAI}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              <FileText className="w-4 h-4" />
              {generatingAI ? 'Generating...' : 'Executive Summary'}
            </button>
            <button
              onClick={() => generateAIAdvisory('transformation_roadmap')}
              disabled={generatingAI}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Target className="w-4 h-4" />
              {generatingAI ? 'Generating...' : 'Roadmap'}
            </button>
          </div>
        </div>

        {aiContent && (
          <div className="bg-white rounded-lg p-6 border border-indigo-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900">
                {aiContent.type === 'executive_summary' ? 'Executive Summary' : 'Transformation Roadmap'}
              </h4>
              <span className="text-xs text-gray-500">AI Generated</span>
            </div>
            <div className="prose prose-sm max-w-none text-gray-700
              prose-headings:text-indigo-800 prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2
              prose-p:mb-3 prose-p:leading-relaxed
              prose-strong:text-gray-900
              prose-ul:my-2 prose-ul:pl-5
              prose-li:my-1
              prose-h3:text-base prose-h3:border-b prose-h3:border-indigo-100 prose-h3:pb-1">
              <ReactMarkdown>{aiContent.content}</ReactMarkdown>
            </div>
          </div>
        )}
      </div>

      {/* Roadmap Summary */}
      {roadmap && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">12-Month Transformation Roadmap</h3>
          
          {/* Quick Wins */}
          {roadmap.quick_wins && roadmap.quick_wins.length > 0 && (
            <div className="mb-6">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-600" />
                Quick Wins (From ERS Opportunities)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {roadmap.quick_wins.map((win, i) => (
                  <div key={i} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 text-sm mb-2">{win.title}</h5>
                    <div className="text-xs text-gray-600 space-y-1">
                      <p>💰 Savings: {win.savings}/year</p>
                      <p>⏱️ Payback: {win.payback}</p>
                      <p className="text-yellow-700 font-medium">{win.impact}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Priority Dimensions */}
          {roadmap.priority_dimensions && roadmap.priority_dimensions.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Priority Areas for Improvement</h4>
              <div className="space-y-3">
                {roadmap.priority_dimensions.map((priority, i) => (
                  <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900">{priority.dimension}</h5>
                        <p className="text-sm text-gray-600 mt-1">
                          Current: Level {priority.current_level} → Target: Level {priority.target_level}
                        </p>
                      </div>
                      <button
                        onClick={() => fetchGapAnalysis(priority.dimension.toLowerCase().replace(/ /g, '_'))}
                        className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-700"
                      >
                        View Actions
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Gap Analysis Modal */}
      {gapAnalysis && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="bg-indigo-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">
                Gap Analysis: {selectedDimension?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </h3>
              <button onClick={() => setGapAnalysis(null)} className="text-white hover:text-gray-200">
                <span className="text-2xl">×</span>
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Current State */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Current State</h4>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-gray-700">Maturity Level:</span>
                  <span className="text-3xl font-bold text-indigo-600">Level {gapAnalysis.current_level}</span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Indicators Completed:</p>
                  <ul className="space-y-2">
                    {gapAnalysis.indicators_completed?.map((indicator, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        {indicator}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Target State */}
              <div className="bg-indigo-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Target State</h4>
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Target Level:</span>
                  <span className="text-3xl font-bold text-indigo-600">Level {gapAnalysis.target_level}</span>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Estimated Timeline: <span className="font-semibold">{gapAnalysis.estimated_timeline}</span>
                </p>
              </div>

              {/* Gap Actions */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Actions Required to Close Gap</h4>
                <div className="space-y-3">
                  {gapAnalysis.gap_actions?.map((action, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between mb-2">
                        <h5 className="font-semibold text-gray-900">{action.action}</h5>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          action.priority === 'High' ? 'bg-red-100 text-red-700' :
                          action.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {action.priority}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <span className="font-medium">Category:</span> {action.category}
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="font-medium">Impact:</span> {action.impact}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setGapAnalysis(null)}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SSuMModule;
